/**
 * Centralized API configuration.
 * All HTTP and WebSocket URLs should use these helpers
 * instead of hardcoding localhost:3002.
 *
 * In development (CRA proxy): relative paths work via setupProxy.js
 * In production (same-origin): relative paths work via Express static serving
 * In remote/mobile: window.location.origin points to the actual server
 */

/**
 * Get the base URL for HTTP API calls.
 * Returns '' (empty string) so fetch('/api/...') uses relative paths.
 * Override with REACT_APP_API_URL env var for external deployments.
 */
export function getApiBaseUrl() {
  // If explicitly set via env var (e.g., for mobile pointing to VPS)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  // Default: relative paths (works with CRA proxy in dev, Express in prod)
  return '';
}

/**
 * Get a full HTTP URL for an API endpoint.
 * @param {string} path - e.g., '/api/devices' or 'api/devices'
 */
export function apiUrl(path) {
  const base = getApiBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

/**
 * Get a WebSocket URL for a given path.
 * Automatically determines ws:// or wss:// based on the current page protocol.
 * @param {string} path - e.g., '/ws/screen-h264/DEVICE_ID'
 */
export function wsUrl(path) {
  // If explicit API URL is set, derive WS from it
  if (process.env.REACT_APP_API_URL) {
    const url = new URL(process.env.REACT_APP_API_URL);
    const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${protocol}//${url.host}${normalizedPath}`;
  }

  // Default: derive from current page location
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host; // includes port if non-standard
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${protocol}//${host}${normalizedPath}`;
}

/**
 * Get a WebSocket URL for cross-workstation commands (port 3003).
 * In production behind nginx, this should be proxied to /ws/remote.
 * @param {string} [host] - optional hostname override
 */
export function wsRemoteUrl(host) {
  if (process.env.REACT_APP_WS_REMOTE_URL) {
    return process.env.REACT_APP_WS_REMOTE_URL;
  }
  // In production, the nginx reverse proxy will route /ws/remote to port 3003
  // In local dev, connect directly to port 3003
  const hostname = host || window.location.hostname || 'localhost';
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

  if (window.location.port === '3001' || window.location.port === '3002') {
    // Local dev — connect directly to port 3003
    return `ws://${hostname}:3003`;
  }
  // Production — go through nginx proxy
  return `${protocol}//${window.location.host}/ws/remote`;
}
